/**
 * responseCompression.js
 *
 * Verbosity limits and phrase-suppression rules per conversational mode.
 * Generates a compact instruction block that gets appended to the system prompt.
 *
 * These are hard constraints, not suggestions. They exist to prevent Kevia
 * from generating long, essay-style responses when the context calls for brevity.
 */

const VERBOSITY_LIMITS = {
  concise:    { maxSentences: 3,  maxParagraphs: 1, allowLists: false },
  pastoral:   { maxSentences: 6,  maxParagraphs: 2, allowLists: false },
  analytical: { maxSentences: 12, maxParagraphs: 3, allowLists: true  },
  reflective: { maxSentences: 10, maxParagraphs: 3, allowLists: false },
  urgent:     { maxSentences: 2,  maxParagraphs: 1, allowLists: false },
  emotional:  { maxSentences: 4,  maxParagraphs: 1, allowLists: false },
};

// Phrases that make Kevia sound like a generic AI chatbot.
// Referenced here for documentation; suppression is enforced via the prompt block.
export const FORBIDDEN_PHRASES = [
  "Claro, con gusto",
  "Con mucho gusto",
  "Por supuesto",
  "Actualmente",
  "He observado que",
  "Basado en los datos",
  "Con base en la información",
  "Como asistente",
  "Permíteme explicarte",
  "Entiendo que preguntas",
  "¡Excelente pregunta!",
  "Me alegra que preguntes",
  "Es importante mencionar",
  "Cabe destacar que",
  "En resumen",
  "En conclusión",
  "Para resumir",
  "A continuación",
  "He identificado",
  "Actualmente cuento con",
  "No dudes en",
  "Espero que esto te ayude",
];

/**
 * Build the verbosity constraint block for the given mode.
 *
 * @param {string} mode
 * @returns {string} — instruction block for system prompt injection
 */
export function buildVerbosityBlock(mode) {
  const lim = VERBOSITY_LIMITS[mode] ?? VERBOSITY_LIMITS.pastoral;

  return [
    `LÍMITES DE RESPUESTA: máximo ${lim.maxSentences} oraciones en ${lim.maxParagraphs} párrafo(s).`,
    lim.allowLists
      ? "Puedes usar lista corta si organiza mejor que prosa."
      : "Sin listas a menos que el pastor las pida explícitamente.",
    "No repitas datos que el pastor ya ve en pantalla.",
    "No abras con frases genéricas (Claro / Por supuesto / Con gusto / Actualmente / etc.).",
    "No cierres con frases de despedida o invitación genérica (No dudes en preguntar / Espero que ayude / etc.).",
    "Detente cuando hayas dicho lo esencial.",
  ].join(" ");
}
